// story: WebGL-проход сюжетных билбордов — видео на плоскости.
//
// Зомби, старуха и призраки — квадраты, всегда развёрнутые к камере по рыску
// (вертикаль остаётся вертикалью), ступни на нижней кромке. Картина —
// плоскость на стене с рамой и холстом, книга — парящий светящийся квадрат.
// Растворение призрака и таяние трупа — порог по шуму (fbm) с горящей
// кромкой. Освещение — как у мешей (окружение 0,4 + до 8 ближайших ламп с
// «обёрнутым» ламбертом), туман и тонмап — те же формулы, что в mesh.frag,
// чтобы существа не выпадали из сцены. Призраки и книга светятся сами.

import type { StoryPassContext } from "../engine/renderer";

const VS = `#version 300 es
layout(location = 0) in vec2 aCorner;
uniform mat4 uViewProj;
uniform vec3 uOrigin;
uniform vec3 uRight;
uniform vec3 uUp;
uniform vec2 uSize;
uniform float uMirror;
uniform vec3 uCameraPos;
uniform float uDepthPull;
out vec2 vUV;
out vec2 vQuad;
out vec3 vWorld;
void main() {
  vec3 p = uOrigin + uRight * (aCorner.x * uSize.x) + uUp * (aCorner.y * uSize.y);
  vWorld = p;
  float u = aCorner.x + 0.5;
  vQuad = vec2(u, aCorner.y);
  if (uMirror > 0.5) u = 1.0 - u;
  // Кадр видео без FLIP_Y: строка 0 — верх картинки → v = 1 - y.
  vUV = vec2(u, 1.0 - aCorner.y);
  vec4 clip = uViewProj * vec4(p, 1.0);
  if (uDepthPull > 0.0) {
    // Глубина — как у точки ближе к камере на uDepthPull (радиус тела):
    // плоскость идёт через центр существа, и ступень внутри его «следа»
    // срезала ноги — на лестнице зомби будто тонул в ней (трейлер, 21.10).
    // Стена ближе радиуса к центру не бывает, сквозь стены не просвечивает.
    vec3 toCam = uCameraPos - p;
    float dl = length(toCam);
    vec4 pulled = uViewProj * vec4(p + toCam / max(dl, 1e-3) * min(uDepthPull, dl * 0.5), 1.0);
    clip.z = pulled.z / pulled.w * clip.w;
  }
  gl_Position = clip;
}`;

const FS = `#version 300 es
precision highp float;
in vec2 vUV;
in vec2 vQuad;
in vec3 vWorld;
uniform sampler2D uTex;
uniform int uHasTex;
uniform int uPacked;      // .pack.mp4 (WebKit): верх кадра — цвет, низ — альфа
uniform int uMode;        // 0 освещён, 1 призрак, 2 картина, 3 книга, 4 тень, 5 картина-ролик в раме
uniform vec3 uTint;
uniform float uAlpha;
uniform float uDissolve;  // 0..1: доля растворённого
uniform vec3 uEdge;
uniform float uFlash;
uniform float uEmissive;
uniform vec3 uNormal;
uniform float uSeed;
uniform float uTime;
uniform vec3 uCameraPos;
uniform int uNumLights;
uniform vec4 uLights[8];
uniform vec3 uLightColors[8];
uniform vec3 uFogColor;
uniform float uFogDensity;
uniform float uFogHeight;
uniform float uFogHeightFalloff;
uniform float uExposure;
out vec4 fragColor;

float hash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}
float vnoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  float a = hash(i), b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0)), d = hash(i + vec2(1.0, 1.0));
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}
float fbm(vec2 p) {
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 4; i++) { v += a * vnoise(p); p *= 2.03; a *= 0.5; }
  return v;
}
vec3 applyFog(vec3 c, vec3 wp) {
  if (uFogDensity <= 0.0) return c;
  float dist = length(uCameraPos - wp);
  float rise = max(0.0, wp.z - uFogHeight);
  float vd = clamp(exp(-rise * uFogHeightFalloff), 0.001, 1.0);
  float f = 1.0 - exp(-uFogDensity * dist * vd);
  return mix(c, uFogColor, clamp(f, 0.0, 1.0));
}
vec3 lightAt(vec3 wp, vec3 n) {
  vec3 l = vec3(0.40);
  for (int i = 0; i < 8; i++) {
    if (i >= uNumLights) break;
    float range = uLights[i].w;
    if (range <= 0.0) continue;
    vec3 tl = uLights[i].xyz - wp;
    float d = length(tl);
    if (d > range) continue;
    float fall = 1.0 - d / range;
    fall *= fall;
    float ndl = max(dot(n, tl / max(d, 1e-3)), 0.0) * 0.55 + 0.45;
    l += uLightColors[i] * ndl * fall;
  }
  return l;
}

vec4 texAt(vec2 uv) {
  if (uPacked == 1) {
    float h = float(textureSize(uTex, 0).y);
    float v = clamp(uv.y, 0.0, 1.0) * 0.5;
    return vec4(texture(uTex, vec2(uv.x, min(v, 0.5 - 1.0 / h))).rgb,
                texture(uTex, vec2(uv.x, max(v + 0.5, 0.5 + 1.0 / h))).r);
  }
  return texture(uTex, uv);
}

void main() {
  if (uMode == 4) {
    // Пятно тени: мягкий эллипс.
    vec2 q = vQuad * 2.0 - 1.0;
    float r = dot(q, q);
    float a = (1.0 - smoothstep(0.25, 1.0, r)) * uAlpha;
    if (a < 0.01) discard;
    fragColor = vec4(0.0, 0.0, 0.0, a);
    return;
  }
  vec3 base;
  float a;
  if (uMode == 5) {
    // Картина целиком из ролика (Kling): рама и холст уже в кадре.
    base = (uHasTex == 1 ? texAt(vec2(vQuad.x, 1.0 - vQuad.y)).rgb : vec3(0.05)) * uTint;
    a = 1.0;
  } else if (uMode == 2) {
    // Картина: золочёная рама + холст + портрет поверх тёмного фона.
    float fw = 0.085;
    vec2 q = vQuad;
    float edgeD = min(min(q.x, 1.0 - q.x), min(q.y, 1.0 - q.y));
    if (edgeD < fw) {
      float t = edgeD / fw;
      float bevel = 0.55 + 0.45 * sin(t * 3.14159);
      float grain = 0.85 + 0.15 * vnoise(q * vec2(90.0, 7.0) + uSeed);
      base = vec3(0.55, 0.40, 0.17) * bevel * grain;
      if (t > 0.82) base *= 0.45; // тёмный паспарту у холста
    } else {
      vec2 iuv = (q - fw) / (1.0 - 2.0 * fw);
      vec2 tuv = vec2(iuv.x, 1.0 - iuv.y);
      vec2 cv = iuv - 0.5;
      vec3 bg = mix(vec3(0.10, 0.075, 0.055), vec3(0.03, 0.025, 0.02), smoothstep(0.1, 0.75, length(cv)));
      bg *= 0.85 + 0.15 * fbm(iuv * 9.0 + uSeed);
      vec4 tex = uHasTex == 1 ? texAt(tuv) : vec4(0.0);
      base = mix(bg, tex.rgb * uTint, tex.a);
      // Кракелюр: тонкие тёмные трещины лака.
      float cr = abs(fbm(iuv * 24.0 + uSeed * 3.0) - 0.5);
      base *= 0.9 + 0.1 * smoothstep(0.0, 0.03, cr);
    }
    a = 1.0;
  } else {
    vec4 tex = uHasTex == 1 ? texAt(vUV) : vec4(0.0);
    base = tex.rgb * uTint;
    a = tex.a;
    if (uMode == 1) {
      // Призрак: блёклый, холодный, светится изнутри; ноги тают в дымке.
      float lum = dot(tex.rgb, vec3(0.299, 0.587, 0.114));
      vec3 pale = uTint * (0.22 + 1.3 * lum);
      base = mix(base, pale, 0.65);
      a *= (0.8 + 0.2 * lum) * smoothstep(0.0, 0.12, vQuad.y);
    }
  }
  if (a < 0.02) discard;

  float glow = uEmissive;
  if (uDissolve > 0.0) {
    float n = fbm(vQuad * vec2(5.0, 9.0) + uSeed);
    float th = uDissolve * 1.08 - 0.04;
    if (n < th) discard;
    float edge = 1.0 - smoothstep(0.0, 0.07, n - th);
    base = mix(base, uEdge, edge);
    glow += edge * 2.5;
  }

  vec3 col;
  if (uMode == 1 || uMode == 3) {
    float flick = 0.92 + 0.08 * sin(uTime * 13.0 + uSeed * 7.0);
    col = base * (0.9 + glow) * flick;
  } else {
    col = base * lightAt(vWorld, uNormal) * uExposure + base * glow;
  }
  col = mix(col, vec3(1.0, 0.35, 0.25), uFlash * 0.55);
  // Призраки и книга светятся сквозь дымку: туман на них вполсилы.
  col = (uMode == 1 || uMode == 3) ? mix(col, applyFog(col, vWorld), 0.5) : applyFog(col, vWorld);
  col = col / (col + vec3(1.4)) * 1.8;
  fragColor = vec4(col, a * uAlpha);
}`;

/** Один квад к отрисовке. */
export interface StoryQuad {
  tex: WebGLTexture | null;
  mode: number;
  ox: number; oy: number; oz: number;
  rx: number; ry: number; rz: number;
  ux: number; uy: number; uz: number;
  w: number; h: number;
  mirror: boolean;
  tint: [number, number, number];
  alpha: number;
  dissolve: number;
  edge: [number, number, number];
  flash: number;
  emissive: number;
  nx: number; ny: number; nz: number;
  seed: number;
  /** Квадрат расстояния до камеры (сортировка от дальних). */
  d2: number;
  /** Текстура — кадр .pack.mp4 (альфа в нижней половине). */
  packed?: boolean;
  /** Насколько ближе к камере считать глубину, м (существа: радиус тела). */
  pull?: number;
}

type U = WebGLUniformLocation | null;

export class StoryRenderer {
  private gl: WebGL2RenderingContext;
  private prog: WebGLProgram | null = null;
  private vao: WebGLVertexArrayObject | null = null;
  private u: Record<string, U> = {};
  private failed = false;

  constructor(gl: WebGL2RenderingContext) {
    this.gl = gl;
  }

  private init(): boolean {
    if (this.prog) return true;
    if (this.failed) return false;
    const gl = this.gl;
    const sh = (type: number, src: string): WebGLShader | null => {
      const s = gl.createShader(type);
      if (!s) return null;
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
        console.warn("[story] shader:", gl.getShaderInfoLog(s));
        return null;
      }
      return s;
    };
    const vs = sh(gl.VERTEX_SHADER, VS);
    const fs = sh(gl.FRAGMENT_SHADER, FS);
    const prog = gl.createProgram();
    if (!vs || !fs || !prog) {
      this.failed = true;
      return false;
    }
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.bindAttribLocation(prog, 0, "aCorner");
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      console.warn("[story] link:", gl.getProgramInfoLog(prog));
      this.failed = true;
      return false;
    }
    this.prog = prog;
    for (const n of [
      "uViewProj", "uOrigin", "uRight", "uUp", "uSize", "uMirror", "uDepthPull", "uTex", "uHasTex", "uPacked", "uMode", "uTint",
      "uAlpha", "uDissolve", "uEdge", "uFlash", "uEmissive", "uNormal", "uSeed", "uTime", "uCameraPos",
      "uNumLights", "uLights", "uLightColors", "uFogColor", "uFogDensity", "uFogHeight",
      "uFogHeightFalloff", "uExposure",
    ]) {
      this.u[n] = gl.getUniformLocation(prog, n);
    }
    const vao = gl.createVertexArray();
    const buf = gl.createBuffer();
    gl.bindVertexArray(vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
      -0.5, 0, 0.5, 0, 0.5, 1,
      -0.5, 0, 0.5, 1, -0.5, 1,
    ]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.bindVertexArray(null);
    this.vao = vao;
    return true;
  }

  /** Нарисовать квады (сортирует от дальних к ближним). */
  draw(ctx: StoryPassContext, quads: StoryQuad[]): void {
    if (quads.length === 0 || !this.init()) return;
    const gl = this.gl;
    const u = this.u;
    quads.sort((a, b) => b.d2 - a.d2);
    gl.useProgram(this.prog);
    gl.bindVertexArray(this.vao);
    gl.uniformMatrix4fv(u.uViewProj!, false, ctx.viewProj);
    gl.uniform3f(u.uCameraPos!, ctx.eye[0]!, ctx.eye[1]!, ctx.eye[2]!);
    gl.uniform1i(u.uNumLights!, ctx.numLights);
    gl.uniform4fv(u.uLights!, ctx.lightPos);
    gl.uniform3fv(u.uLightColors!, ctx.lightCol);
    gl.uniform3f(u.uFogColor!, ctx.fogColor[0]!, ctx.fogColor[1]!, ctx.fogColor[2]!);
    gl.uniform1f(u.uFogDensity!, ctx.fogDensity);
    gl.uniform1f(u.uFogHeight!, ctx.fogHeight);
    gl.uniform1f(u.uFogHeightFalloff!, ctx.fogHeightFalloff);
    gl.uniform1f(u.uExposure!, ctx.exposure);
    gl.uniform1f(u.uTime!, ctx.time);
    gl.uniform1i(u.uTex!, 0);
    gl.activeTexture(gl.TEXTURE0);
    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LEQUAL);
    gl.depthMask(false);
    gl.disable(gl.CULL_FACE);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    for (const q of quads) {
      gl.uniform3f(u.uOrigin!, q.ox, q.oy, q.oz);
      gl.uniform3f(u.uRight!, q.rx, q.ry, q.rz);
      gl.uniform3f(u.uUp!, q.ux, q.uy, q.uz);
      gl.uniform2f(u.uSize!, q.w, q.h);
      gl.uniform1f(u.uMirror!, q.mirror ? 1 : 0);
      gl.uniform1f(u.uDepthPull!, q.pull ?? 0);
      gl.uniform1i(u.uMode!, q.mode);
      gl.uniform1i(u.uHasTex!, q.tex ? 1 : 0);
      gl.uniform1i(u.uPacked!, q.packed ? 1 : 0);
      if (q.tex) gl.bindTexture(gl.TEXTURE_2D, q.tex);
      gl.uniform3f(u.uTint!, q.tint[0], q.tint[1], q.tint[2]);
      gl.uniform1f(u.uAlpha!, q.alpha);
      gl.uniform1f(u.uDissolve!, q.dissolve);
      gl.uniform3f(u.uEdge!, q.edge[0], q.edge[1], q.edge[2]);
      gl.uniform1f(u.uFlash!, q.flash);
      gl.uniform1f(u.uEmissive!, q.emissive);
      gl.uniform3f(u.uNormal!, q.nx, q.ny, q.nz);
      gl.uniform1f(u.uSeed!, q.seed);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
    }
    gl.bindTexture(gl.TEXTURE_2D, null);
  }
}
